// Precedence level mapping from database
// Matches the precedence table: name -> precedence value
export const PRECEDENCE_LEVELS = {
  default: 0,
  partner: 100,
  deployment: 200,
  regional: 300,
  environment: 400,
  feature: 500,
  tenant: 1000,
} as const

export type PresetType = keyof typeof PRECEDENCE_LEVELS

export interface JsonPatchOperation {
  op: string
  path: string
  value?: any
  from?: string
  presetContext?: {
    presetId: number
    presetName: string
    presetType: string // String preset type name (e.g., 'partner', 'deployment')
  }
}

export interface JsonPatchDocument {
  operations: JsonPatchOperation[]
}

export interface ConfigurationProperty {
  key: string
  label: string
  value: any
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object' | 'group'
  description?: string
  readonly?: boolean
  required?: boolean
  enum?: string[]
  minimum?: number
  maximum?: number
}

export interface Tenant {
  id: number
  name: string
  displayName: string
  description?: string
  isActive: boolean
  updatedAt: string
  json?: string // JSON column containing preset assignments and tenant-level config
}

export interface PrecedenceLevel {
  name: string
  description: string
  precedence: number
}

export interface PrecedenceLevelsResponse {
  levels: PrecedenceLevel[]
}

export interface PresetSummary {
  presetId: number
  presetName: string
  presetType: string // Changed from number to string
  precedence: number // Changed from priority to precedence (0, 100-500, 1000)
  priority?: number // Legacy field name - backend may still send this during migration
  hasOverrides: boolean
}

export interface PresetInfo {
  presetId: number
  presetName: string
  presetType: string
  precedence?: number
  description?: string
  hasTenantOverrides: boolean
}

export interface PresetConfiguration {
  presetInfo: PresetInfo
  baseConfiguration: any
  tenantOverrides: any
  inheritedConfiguration: any
  effectiveConfiguration: any
  templateVariables: string[]
  changesSummary: {
    addedProperties: string[]
    modifiedProperties: string[]
    removedProperties: string[]
    conflictsResolved: string[]
  }
  resolvedAt: string
  eTag: string
}

export interface FlatteningMetadata {
  flattenedGroups: string[]
  propertySources: Record<string, string>
  conflictsResolved: Record<string, string>
  preservedRootProperties: string[]
}

export interface VaultEntry {
  provider: string
  uri: string
}

export interface SecretReferenceInner {
  vault?: string
  ref: string
  version?: string
  _resolvedVaultUri?: string
}

export interface SecretReferenceValue {
  $secret: SecretReferenceInner
}

export interface SecretManifestEntry {
  path: string
  vault: string
  vaultUri?: string
  ref: string
  version?: string
}

export interface ResolvedConfiguration {
  tenantId: string
  configuration: any // JsonDocument - fully merged configuration
  resolvedAt: string
  eTag: string
  presetSummary: PresetSummary[]
  flatteningMetadata?: FlatteningMetadata
  TenantConfiguration?: any // Raw tenant-level configuration overrides (Schema V3) - backend uses capital T
  secretManifest?: SecretManifestEntry[]
}

export interface JsonPatchResponse {
  success: boolean
  configuration?: ResolvedConfiguration
  eTag?: string
  operationsApplied: number
  totalOperations?: number
  message?: string
  correlationId?: string
  presetOperations?: PresetPatchOperation[]
}

export interface PresetPatchOperation {
  presetId: number
  presetName: string
  operation: JsonPatchOperation
  success: boolean
}

export interface PresetPatchResult {
  success: boolean
  errors: ApiErrorResponse[]
  totalOperations: number
  operationsApplied?: number
  eTag?: string
  correlationId?: string
  presetOperations?: PresetPatchOperation[]
}

export interface ConfigurationCacheStatistics {
  hitCount: number
  missCount: number
  hitRate: number
  entryCount: number
  estimatedSize: number
  lastCompactionTime?: string
}

export interface ApiErrorResponse {
  code: string
  message: string
  details?: any
  timestamp?: string
  path?: string
  correlationId?: string
  timestampValue: string
}

export interface UserInfo {
  id: number
  email: string
  displayName?: string
  role: UserRole
  scopes?: string[]
  isActive: boolean
  updatedAt: string
}

/**
 * @deprecated Use RoleIds from '@/types/roles' instead
 * Legacy UserRole enum - kept for backwards compatibility
 * The new RBAC system uses role IDs (1-8) instead of this enum
 */
export const UserRole = {
  Viewer: 0,
  Contributor: 1,
  Administrator: 2
} as const

/**
 * @deprecated Use Role type from '@/types/roles' instead
 */
export type UserRole = typeof UserRole[keyof typeof UserRole]

export interface AuditEntry {
  sessionId: string
  tenantId: string
  userId: number
  operation: JsonPatchOperation
  timestamp: string
  correlationId?: string
}

export interface ValidationOptions {
  validateMarkdownDescription?: boolean
  validateEditPresentation?: boolean
  validateVisibility?: boolean
  validateCrossReferences?: boolean
  userRoles?: string[]
  tenantType?: string
  featureFlags?: Record<string, boolean>
}

export interface SchemaValidationRequest {
  json: string
  schema: string
  validateMarkdownDescription?: boolean
  validateEditPresentation?: boolean
  validateVisibility?: boolean
  validateCrossReferences?: boolean
  userRoles?: string[]
  tenantType?: string
  featureFlags?: Record<string, boolean>
}

export interface TemplateTestRequest {
  jsonConfig: string
  maxDepth?: number
}

export interface TemplateTestError {
  code: string
  message: string
  jsonPath: string
  templateReference?: string
  details?: any
}

export interface TemplateTestWarning {
  code: string
  message: string
  jsonPath: string
  templateReference?: string
  details?: any
}

export interface TemplateTestResponse {
  original: any
  resolved: any | null
  success: boolean
  substitutionCount: number
  maxDepthReached: number
  errors: TemplateTestError[]
  warnings: TemplateTestWarning[]
}

export interface EditLockState {
  isLocked: boolean
  lockHolder?: string
  lockAcquiredAt?: string
  eTag?: string
}

export interface ConfigurationEditorState {
  selectedTenant?: string
  selectedPreset?: number
  editMode: boolean
  lockState: EditLockState
  isDirty: boolean
  validationErrors: any[]
  currentView: 'json' | 'form' | 'preview'
}

// Template Introspection Types
export interface TemplateErrorDetail {
  code: string
  message: string
  jsonPath: string
  templateReference?: string
  details?: any
}

export interface TemplateWarningDetail {
  code: string
  message: string
  jsonPath: string
  templateReference?: string
}

export interface TemplateVariableResolution {
  templateReference: string
  referencedPath: string
  usageJsonPath: string
  resolved: boolean
  usageResolvedValue?: string
  referencedResolvedValue?: string
  errorCode?: string
  errorMessage?: string
  caseInsensitiveMatch: boolean
  warningCodes: string[]
}

export interface TemplateIntrospection {
  tenantId: string
  resolvedAt: string
  etag: string
  original: any
  resolved: any | null
  templateCount: number
  substitutionCount: number
  errors: TemplateErrorDetail[]
  warnings: TemplateWarningDetail[]
  variables: TemplateVariableResolution[]
}

export interface CreateTenantRequest {
  displayName: string
  name?: string
  description?: string
  presetAssignments?: Record<string, number>
  sourceTenantName?: string
  copySourcePresetAssignments?: boolean
  copyTenantConfiguration?: boolean
}
